import { CalendarDays } from 'lucide-react';
import { CTA_GRADIENT } from '@/features/billboards/constants/detailTheme';
import { resolveCampaignCtaLabel } from '@/features/billboards/utils/billboardDetailUtils';
import { BillboardDetailData } from '../../api/billboardApi';

type BookingPanelProps = {
  billboard: BillboardDetailData;
  basePrice?: number;
  currency: string;
  startDate: string;
  /** Earliest launch date — today only for instant-ad accounts on an online screen. */
  minStartDate: string;
  isScreenOffline?: boolean;
  hasToken: boolean;
  canCreate: boolean;
  isClient?: boolean;
  onStartDateChange: (value: string) => void;
  onCreate: () => void;
};

export function BookingPanel({
  basePrice,
  currency,
  startDate,
  minStartDate,
  isScreenOffline = false,
  hasToken,
  canCreate,
  isClient = false,
  onStartDateChange,
  onCreate,
}: BookingPanelProps) {
  const disabled = hasToken && !canCreate && !isClient;
  const dateTooEarly = Boolean(startDate) && startDate < minStartDate;

  /* Modified by: Ishan | Soft-contrast pricing and launch date sidebar card */
  return (
    <aside className="bg-white rounded-[24px] p-[22px] shadow-[0_10px_30px_rgba(60,40,120,0.05)] border border-[#EEEBF8] self-start">
      <p className="text-[11px] font-extrabold tracking-[1px] text-[#6A6688] uppercase">
        BASE PRICE
      </p>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="text-[34px] font-extrabold tracking-tight text-[#3A3660]">
          {basePrice ?? 0}
        </span>
        <span className="text-[14px] font-semibold text-[#6A6688]">
          {currency} /sec
        </span>
      </div>

      <p className="mt-2 text-[13px] leading-[1.5] text-[#6A6688]">
        {isClient
          ? 'If you want to run ads yourself, register as a personal advertiser.'
          : 'Schedule at least 48 hours ahead for smoother approvals.'}
      </p>

      {!isClient ? (
        <>
          <label className="mt-4 mb-2 block text-left text-[13px] font-bold text-[#3A3660] uppercase">
            LAUNCH DATE
          </label>
          <div
            className={`flex h-[48px] items-center gap-2 rounded-[14px] border bg-white px-3.5 ${
              dateTooEarly ? 'border-red-300' : 'border-[#EAE8F5]'
            }`}
          >
            <CalendarDays size={16} className="shrink-0 text-[#8156B4]" />
            <input
              type="date"
              value={startDate}
              min={minStartDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full border-none bg-transparent text-[14px] font-medium text-[#3A3660] outline-none"
            />
          </div>
          {dateTooEarly ? (
            <p className="mt-2 mb-0 text-xs font-semibold text-red-600">
              {isScreenOffline
                ? 'This screen is offline right now, so same-day launches are unavailable. Pick a later date to continue.'
                : `Your account can launch from ${minStartDate} onwards. Pick a later date to continue.`}
            </p>
          ) : null}
        </>
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={onCreate}
        className="mt-4 hidden w-full cursor-pointer rounded-[14px] h-[50px] text-[14px] font-bold text-white transition-all shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:bg-[#EAE8F5] disabled:text-[#8E8AA8] lg:block"
        style={disabled ? undefined : { background: CTA_GRADIENT }}
      >
        {resolveCampaignCtaLabel(hasToken, canCreate, false, isClient)}
      </button>
    </aside>
  );
}
